"use client"

import {
  IconCalendarCheck,
  IconCalendarPlus,
  IconCar,
  IconChevronRight,
  IconInfoCircle,
  IconPlaneArrival,
  IconUserPlus,
} from "@tabler/icons-react"
import { useTranslations } from "next-intl"

import { Kbd, LegendRow, LegendSection } from "@/components/calendar/calendar-legend"
import { Button } from "@/components/ui/button"
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover"
import { cn } from "@/lib/utils"
import { useAuthStore } from "@/stores/auth-store"
import { calendarPreset } from "@/lib/appointment-presets"

import {
  ARRIVAL_VISIT_LABEL_KEY,
  ARRIVAL_VISIT_SWATCH,
  LATE_ARRIVAL_FROM,
  ARRIVAL_VISIT_TONE,
  REASON_ICON,
  REASON_TONE,
  URGENCY_RAIL,
  type Reason,
  type Urgency,
  type useNeedsBooking,
} from "@/components/dashboard/variants/frontdesk/use-needs-booking"

/** Static literal keys per reason (T-72-01: never t(someVar)). */
const REASON_LEGEND_KEY: Record<Reason, string> = {
  arriving: "frontdeskBooking.legend.reasons.arriving",
  landed: "frontdeskBooking.legend.reasons.landed",
  departing: "frontdeskBooking.legend.reasons.departing",
  no_trip: "frontdeskBooking.legend.reasons.noTrip",
  stage: "frontdeskBooking.legend.reasons.stage",
  undated: "frontdeskBooking.legend.reasons.undated",
  lab: "frontdeskBooking.legend.reasons.lab",
  delivered: "frontdeskBooking.legend.reasons.delivered",
  invoice: "frontdeskBooking.legend.reasons.invoice",
  missed: "frontdeskBooking.legend.reasons.missed",
  task: "frontdeskBooking.legend.reasons.task",
}

/** Arrivals and departures without the Driver column only book visits. */
const REASON_LEGEND_KEY_NO_DRIVES: Partial<Record<Reason, string>> = {
  arriving: "frontdeskBooking.legend.reasons.arrivingVisit",
  departing: "frontdeskBooking.legend.reasons.departingVisit",
}

/**
 * The logistics desk's wording for the only two reasons it has (Radiance
 * 261001): it puts a driver on the ride and checks the patient in — it books
 * no visit.
 */
const LOGISTICS_REASON_LEGEND_KEY: Partial<Record<Reason, string>> = {
  arriving: "frontdeskBooking.legend.reasonsLogistics.arriving",
  departing: "frontdeskBooking.legend.reasonsLogistics.departing",
}

const URGENCY_ORDER: Urgency[] = ["late", "today", "close", "near", "later"]

const URGENCY_LEGEND_KEY: Record<Urgency, string> = {
  late: "frontdeskBooking.legend.urgency.late",
  today: "frontdeskBooking.legend.urgency.today",
  close: "frontdeskBooking.legend.urgency.close",
  near: "frontdeskBooking.legend.urgency.near",
  later: "frontdeskBooking.legend.urgency.later",
}

const VISIT_BUCKETS = [1, 2, 3, 4] as const

/**
 * Dentour 261015 — with visit SWATCHES the third and later visits share one
 * brown, so the legend lists three rows, the last one "returning".
 */
const VISIT_SWATCH_ROWS: ReadonlyArray<{ bucket: 1 | 2 | 3; key: string }> = [
  { bucket: 1, key: "frontdeskBooking.visit.first" },
  { bucket: 2, key: "frontdeskBooking.visit.second" },
  { bucket: 3, key: "frontdeskBooking.visit.returning" },
]

/** The row's left bar, as the table draws it. */
function Rail({ className }: { className: string }) {
  return <span className={cn("h-4 w-[3px] rounded-full", className)} />
}

/**
 * The desk board's legend — the same info-button popover the calendar has
 * ({@link import('@/components/calendar/calendar-legend').CalendarLegend}),
 * built from its row and section pieces, decoding every icon and colour a row
 * can carry: the reason icon, the arrival plane's visit colour, the urgency
 * bar, the booked / plan / book markers, the Driver chips and the care-team
 * slots. Every swatch is drawn from the same maps the table renders with, so
 * the legend cannot drift from the rows.
 *
 * Like the calendar's, it lists only what the board is actually showing: the
 * visit colours only when the server numbered the arrivals, the Driver and
 * Care team sections only when those columns are on screen.
 */
export function NeedsBookingLegend({ board }: { board: ReturnType<typeof useNeedsBooking> }) {
  const t = useTranslations("dashboard")
  const {
    showDrivers,
    showCareTeam,
    hasVisitOrdinals,
    reasons,
    checkInMode,
    showIdentity,
    showDeposits,
    showLateArrivals,
  } = board
  // The flag, not showDrivers: a drive-enabled board with no travel rows in
  // the window must still describe arrivals by their pickup.
  const drivesHidden =
    useAuthStore((s) => s.user?.feature_flags?.booking_board_hide_drives_enabled) ?? false
  const visitSwatches = useAuthStore((s) =>
    calendarPreset(s.user?.appointment_presets, "travel_visit_colors"),
  )

  return (
    <Popover>
      <PopoverTrigger asChild>
        <Button
          variant="outline"
          size="icon"
          className="size-8"
          aria-label={t("frontdeskBooking.legend.trigger")}
          title={t("frontdeskBooking.legend.trigger")}
        >
          <IconInfoCircle className="h-4 w-4" />
        </Button>
      </PopoverTrigger>
      <PopoverContent align="end" className="scrollbar-thin max-h-[80vh] w-80 overflow-y-auto">
        <div className="space-y-3">
          <p className="text-sm font-semibold">{t("frontdeskBooking.legend.title")}</p>

          <LegendSection title={t("frontdeskBooking.legend.reasonsTitle")}>
            {reasons.map((reason) => {
              const Icon = REASON_ICON[reason]
              return (
                <LegendRow
                  key={reason}
                  swatch={<Icon className={cn("size-4", REASON_TONE[reason])} />}
                  label={t(
                    (checkInMode && LOGISTICS_REASON_LEGEND_KEY[reason]) ||
                      (drivesHidden && REASON_LEGEND_KEY_NO_DRIVES[reason]) ||
                      REASON_LEGEND_KEY[reason],
                  )}
                />
              )
            })}
          </LegendSection>

          {hasVisitOrdinals && (
            <LegendSection title={t("frontdeskBooking.legend.visitTitle")}>
              {visitSwatches
                ? VISIT_SWATCH_ROWS.map(({ bucket, key }) => (
                    <LegendRow
                      key={bucket}
                      swatch={
                        <span
                          className={cn(
                            "flex size-4 items-center justify-center rounded-sm",
                            ARRIVAL_VISIT_SWATCH[bucket],
                          )}
                        >
                          <IconPlaneArrival className="size-3" />
                        </span>
                      }
                      label={t(key)}
                    />
                  ))
                : VISIT_BUCKETS.map((bucket) => (
                    <LegendRow
                      key={bucket}
                      swatch={<IconPlaneArrival className={cn("size-4", ARRIVAL_VISIT_TONE[bucket])} />}
                      label={t(ARRIVAL_VISIT_LABEL_KEY[bucket])}
                    />
                  ))}
            </LegendSection>
          )}

          <LegendSection title={t("frontdeskBooking.legend.urgencyTitle")}>
            {URGENCY_ORDER.map((urgency) => (
              <LegendRow
                key={urgency}
                swatch={<Rail className={URGENCY_RAIL[urgency]} />}
                label={t(URGENCY_LEGEND_KEY[urgency])}
              />
            ))}
            <LegendRow
              swatch={<Rail className="bg-emerald-500/40" />}
              label={t(checkInMode ? "frontdeskBooking.legend.urgency.done" : "frontdeskBooking.legend.urgency.booked")}
            />
          </LegendSection>

          <LegendSection title={t("frontdeskBooking.legend.markersTitle")}>
            <LegendRow
              swatch={<IconCalendarCheck className="size-3.5 text-emerald-600 dark:text-emerald-400" />}
              label={t(checkInMode ? "frontdeskBooking.legend.done" : "frontdeskBooking.legend.booked")}
            />
            <LegendRow
              swatch={<IconChevronRight className="size-3.5 text-text-dim" />}
              label={t("frontdeskBooking.legend.plan")}
            />
            {/* Radiance 261005 — a late landing: the visit waits for tomorrow. */}
            {checkInMode && showLateArrivals && (
              <LegendRow
                swatch={
                  <span className="rounded-full border border-sky-500/40 px-0.5 text-[8.5px] font-medium leading-3 text-sky-700 dark:text-sky-400">
                    +1
                  </span>
                }
                label={t("frontdeskBooking.legend.lateArrival", { time: LATE_ARRIVAL_FROM })}
              />
            )}
            {/* Radiance 261005 — the Kapar, drawn as the row draws it. */}
            {showDeposits && (
              <LegendRow
                swatch={
                  <span className="rounded-full border border-success/30 bg-success-tint px-0.5 text-[8.5px] font-medium leading-3 text-success">
                    €
                  </span>
                }
                label={t("frontdeskBooking.legend.deposit")}
              />
            )}
            {/* The logistics desk books nothing: its Book is the check-in,
                explained in a section of its own below. */}
            {!checkInMode && (
              <LegendRow
                swatch={<IconCalendarPlus className="size-4" />}
                label={t("frontdeskBooking.legend.book")}
              />
            )}
          </LegendSection>

          {/* Radiance 261001 — the logistics desk's own column: the flight
              check-in, drawn as the toggle draws it, and the ID it needs.
              Swatches stay inside the 16px slot every other row uses. */}
          {checkInMode && (
            <LegendSection title={t("frontdeskBooking.legend.checkInTitle")}>
              <LegendRow
                swatch={
                  <span className="rounded-[3px] bg-emerald-500/15 px-0.5 text-[8.5px] font-medium leading-3.5 text-emerald-700 dark:text-emerald-400">
                    {t("frontdeskBooking.checkIn.yes")}
                  </span>
                }
                label={t("frontdeskBooking.legend.checkInYes")}
              />
              <LegendRow
                swatch={
                  <span className="rounded-[3px] bg-surface-2 px-0.5 text-[8.5px] font-medium leading-3.5 text-foreground">
                    {t("frontdeskBooking.checkIn.no")}
                  </span>
                }
                label={t("frontdeskBooking.legend.checkInNo")}
              />
              {showIdentity && (
                <LegendRow
                  swatch={
                    <span className="rounded-full border border-amber-500/40 px-0.5 text-[8.5px] font-medium leading-3 text-amber-700 dark:text-amber-400">
                      ID
                    </span>
                  }
                  label={t("frontdeskBooking.legend.noId")}
                />
              )}
            </LegendSection>
          )}

          {showDrivers && (
            <LegendSection title={t("frontdeskBooking.legend.driverTitle")}>
              <LegendRow
                swatch={
                  <span className="flex h-3.5 w-4 items-center justify-center rounded-full bg-emerald-100 text-emerald-800 dark:bg-emerald-900/40 dark:text-emerald-200">
                    <IconCar className="size-2.5" />
                  </span>
                }
                label={t("frontdeskBooking.legend.driverAssigned")}
              />
              <LegendRow
                swatch={
                  <span className="flex h-3.5 w-4 items-center justify-center rounded-full bg-amber-100 text-amber-800 dark:bg-amber-900/40 dark:text-amber-200">
                    <IconCar className="size-2.5" />
                  </span>
                }
                label={t("frontdeskBooking.legend.driverMissing")}
              />
              <LegendRow
                swatch={
                  <span className="flex size-4 items-center justify-center rounded border border-border">
                    <IconCar className="size-2.5" />
                  </span>
                }
                label={t("frontdeskBooking.legend.driverAction")}
              />
              <LegendRow
                swatch={<span className="text-[11px] text-text-dim">—</span>}
                label={t("frontdeskBooking.legend.driverNone")}
              />
            </LegendSection>
          )}

          {showCareTeam && (
            <LegendSection title={t("frontdeskBooking.legend.careTeamTitle")}>
              <LegendRow
                swatch={
                  <span className="flex flex-col items-start gap-0.5">
                    <span className="h-[2px] w-3 rounded-full bg-foreground" />
                    <span className="h-[2px] w-2.5 rounded-full bg-text-dim" />
                  </span>
                }
                label={t("frontdeskBooking.legend.careTeamStack")}
              />
              <LegendRow
                swatch={<IconUserPlus className="size-3.5 text-text-dim" />}
                label={t("frontdeskBooking.legend.careTeamAssign")}
              />
            </LegendSection>
          )}

          <LegendSection title={t("frontdeskBooking.legend.keyboardTitle")}>
            <div className="flex items-center gap-2 text-xs text-muted-foreground">
              <Kbd>Enter</Kbd>
              <span>{t("frontdeskBooking.legend.keyboardOpen")}</span>
            </div>
          </LegendSection>
        </div>
      </PopoverContent>
    </Popover>
  )
}
